import { formatReal } from '@cannect/utils/formatters'
import { TProgressiveTier } from './types'

/**
 * Faixas exibíveis na PDP: descarta a faixa base sem nenhum benefício
 * (discount_percent = 0 no item E sem shipping_value no frete), que representa
 * o preço cheio e não deve aparecer como benefício (FR-004). Mantém faixas de
 * regras 'shipping' (discount_percent sempre 0, benefício só em shipping_value).
 * NÃO calcula desconto — apenas filtra o que a API retornou.
 */
export const getDisplayableTiers = (tiers?: TProgressiveTier[]): TProgressiveTier[] => {
  if (!tiers?.length) return []
  return tiers.filter((tier) => Number(tier.discount_percent) > 0 || Number(tier.shipping_value) > 0)
}

/**
 * Rótulo do benefício de FRETE de uma faixa: "frete grátis" (percent ≥ 100), "X% no frete" (percent)
 * ou "R$ X no frete" (value). Mesma semântica usada no card de grupo (ProgressiveGroupSection) para
 * regras benefit_target='shipping', onde discount_percent é sempre 0 (item não baixa de preço).
 */
export const formatShippingTierLabel = (
  tier: Pick<TProgressiveTier, 'shipping_discount_type' | 'shipping_value'>
): string => {
  if (tier.shipping_discount_type === 'value') {
    return `${(formatReal(Number(tier.shipping_value) || 0) as string) || 'R$ --'} OFF no frete`
  }
  return Number(tier.shipping_value) >= 100 ? 'frete grátis' : `${tier.shipping_value}% OFF no frete`
}

/**
 * Rótulo de quantidade da faixa. Faixas abertas (open_ended) são exibidas como "X ou mais".
 */
export const formatTierQuantityLabel = (tier: TProgressiveTier): string => {
  const unitLabel = tier.min_quantity === 1 ? 'un' : 'un'
  if (tier.open_ended) return `${tier.min_quantity} ${unitLabel} ou mais`
  return `${tier.min_quantity} ${unitLabel}`
}

/**
 * Normaliza o desconto progressivo presente no payload do pedido para um total numérico.
 * Shape CONFIRMADO pela API (contratos 004): `progressive_discount` é um ARRAY de aplicações,
 * cada uma com `total_discount` (ou `null` quando não há desconto). Mantém fallback defensivo
 * para escalar / `{ total }` por segurança. Nunca recalcula nada — apenas soma o que a API devolveu.
 */
export const getProgressiveDiscountTotal = (
  progressiveDiscount?: number | { total?: number } | Array<{ total_discount?: number }> | null
): number => {
  if (progressiveDiscount == null) return 0
  if (typeof progressiveDiscount === 'number') return progressiveDiscount
  if (Array.isArray(progressiveDiscount)) {
    return progressiveDiscount.reduce((sum, app) => sum + Number(app?.total_discount ?? 0), 0)
  }
  return Number(progressiveDiscount.total ?? 0)
}

/**
 * Determina o rótulo da linha de desconto progressivo.
 * Quando alguma aplicação tem benefit_target='shipping', indica abatimento no custo de importação (FR-016).
 * Leitura defensiva — aceita o array confirmado ou shapes antigos.
 */
export const getProgressiveDiscountLabel = (progressiveDiscount?: unknown): string => {
  const SHIPPING_LABEL = 'Desconto progressivo (custo de importação)'
  const DEFAULT_LABEL = 'Desconto progressivo'
  if (!progressiveDiscount || typeof progressiveDiscount === 'number') return DEFAULT_LABEL
  if (Array.isArray(progressiveDiscount)) {
    return progressiveDiscount.some((app: any) => app?.benefit_target === 'shipping') ? SHIPPING_LABEL : DEFAULT_LABEL
  }
  const pd = progressiveDiscount as Record<string, any>
  if (pd.benefit_target === 'shipping') return SHIPPING_LABEL
  const apps: any[] = pd.applications ?? []
  return apps.some((app) => app?.benefit_target === 'shipping') ? SHIPPING_LABEL : DEFAULT_LABEL
}
